import { UserProfile } from 'api/account/models/UserProfile';
import { Paged } from 'api/common/types/Page';
import ApiTokensService from 'api/apiTokens/ApiTokensService';
import { ApiTokenDto } from 'api/apiTokens/models/ApiTokenDto';
import { ApiTokenSearchCriteria } from 'api/apiTokens/models/ApiTokenSearchCriteria';
import UsersService from 'api/users/UsersService';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEllipsisH } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { Reducers } from 'store/types';

import styles from './ApiTokensList.module.scss';

type Props = {
};

const ApiTokensList: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    
    const [apitokensPage, setApiTokensPage] = useState<Paged<ApiTokenDto>>();
    const [criteria, setCriteria] = useState<ApiTokenSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'api_key',
        orderBy: 'asc'
    });


    const [itemToRemove, setItemToRemove] = useState<ApiTokenDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasApiTokensWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_API_TOKENS_WRITE']);

    const showRemoveItemDialog = (item: ApiTokenDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    };

    const renderTableActionCell = (
        row: ApiTokenDto,
    ) => {
        return (
            <Dropdown
                options={
                    <>                        
                        {hasApiTokensWritePolicy && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<ApiTokenDto>[] = [
        {
            field: 'id',
            name: t('api_tokens.list.user'),
            onSearch: onTableFilter,
            searchField: 'id',
            renderCell: row => <>{row.id}</>
        },
        {
            field: 'apiKey',
            name: t('api_tokens.list.key'),
            onSearch: onTableFilter,
            searchField: 'apiKey',
            renderCell: row => <>{row.apiKey}</>
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' },
        }
    ];

    const getData = async () => {
        try {
            Loading.show();

            const page = await ApiTokensService.getList(criteria);
            setApiTokensPage(page);

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get vehicle segments list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await ApiTokensService.remove(itemToRemove);
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete apitoken`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };


     const onNew = () => {        

        try {
            ApiTokensService.create()
            .then(() => {
                    setCriteria({ ...criteria, page: 1 });
            });
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't save apitoken`, error);
            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
        }
    };


    useEffect(() => {
            getData()
    }, [criteria]);

    
    return (
        <div className='list'>
            <div className={styles.tabHeader}>
                <div>
                 
                </div>
                <div className={styles.rowFlex}>
                    {hasApiTokensWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={onNew} />}
                </div>
            </div>
            {<ListingTable
                columns={tableColumns}
                rows={apitokensPage?.items || []}                
                allowHover={true}
                initialSearch={{ colField: criteria.orderColumn ?? 'l.name', isOrderAsc: (criteria.orderBy && criteria.orderBy == 'asc') ? true : false }}
            />}
            <PaginationWithInfo
                itemName={t('api_tokens.list.title')}
                currentPage={apitokensPage?.currentPage || 1}
                pageItems={apitokensPage?.items.length || 0}
                totalItems={apitokensPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })}
            />
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record')} />
        </div>
    );
};

export default ApiTokensList;
